import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Award, Download, Edit, Calendar, Shield, AlertTriangle } from 'lucide-react';

export function CurriculumCertificatesTab({ certificates, onDownload, onShowUpdate }) {
  if (!certificates || certificates.length === 0) {
    return (
      <div className="text-center py-12">
        <Award className="h-12 w-12 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">No certificates issued yet</p>
        <p className="text-xs text-slate-400 mt-2">
          Certificates are issued after curriculum approval
        </p>
      </div>
    );
  }

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', label: 'Unknown', color: 'bg-slate-100 text-slate-700' };
    
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'bg-red-100 text-red-700' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'critical', label: `${daysUntilExpiry} days left`, color: 'bg-amber-100 text-amber-700' };
    } else if (daysUntilExpiry <= 90) {
      return { status: 'warning', label: `${daysUntilExpiry} days left`, color: 'bg-yellow-100 text-yellow-700' };
    } else {
      return { status: 'valid', label: 'Valid', color: 'bg-green-100 text-green-700' };
    }
  };

  return (
    <div className="space-y-4">
      {certificates.map((cert) => {
        const expiryInfo = getExpiryStatus(cert.expiry_date);
        
        return (
          <Card key={cert.id} className="overflow-hidden">
            <div className="flex">
              {/* Certificate Badge */}
              <div className="w-24 bg-gradient-to-b from-[#1B2A4A] to-[#2A3F6A] flex items-center justify-center">
                <Award className="h-10 w-10 text-[#C5A572]" />
              </div>
              
              <CardContent className="flex-1 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-[#1B2A4A] mb-1">{cert.curriculum_title}</h3>
                    <p className="text-sm text-slate-600">{cert.institution_name}</p>
                    
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
                      <span className="flex items-center gap-1 text-slate-500">
                        <Calendar className="h-3 w-3" />
                        Issued: {new Date(cert.issue_date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1 text-slate-500">
                        <Calendar className="h-3 w-3" />
                        Expires: {cert.expiry_date ? new Date(cert.expiry_date).toLocaleDateString() : 'N/A'}
                      </span>
                      <Badge className={expiryInfo.color}>
                        {expiryInfo.status === 'expired' && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {expiryInfo.status === 'valid' && <Shield className="h-3 w-3 mr-1" />}
                        {expiryInfo.label}
                      </Badge>
                    </div>
                    
                    {cert.certificate_number && (
                      <p className="text-xs text-slate-400 mt-2 font-mono">
                        Certificate No: {cert.certificate_number}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button 
                      size="sm" 
                      className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                      onClick={() => onDownload(cert.id)}
                    >
                      <Download className="h-4 w-4 mr-1" /> Download
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="rounded-sm"
                      onClick={() => onShowUpdate(cert.id)}
                    >
                      <Edit className="h-4 w-4 mr-1" /> Update
                    </Button>
                  </div>
                </div>
              </CardContent>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
